<template>
  <header :class="['app-navbar', { scrolled: isScrolled }]" id="navbar">
    <div class="nav-inner">
      <!-- 品牌 -->
      <router-link to="/home" class="brand">
        <span class="logo"></span>
        <span>
          <span class="brand-cn">易隆创</span>
          <span class="brand-en">YILONGCHUANG</span>
        </span>
      </router-link>

      <!-- 桌面导航 -->
      <nav class="desktop-nav">
        <router-link to="/home" class="nav-link" active-class="active" exact>首页</router-link>

        <!-- 生态下拉 -->
        <div class="nav-group">
          <button class="eco-nav-btn">
            四大生态 <i class="el-icon-arrow-down chev"></i>
          </button>
          <div class="eco-menu">
            <button
              v-for="eco in ecosystems"
              :key="eco.slug"
              @click="$router.push(`/ecosystem/${eco.slug}`)"
            >
              <small>{{ eco.index }}</small>
              <strong>{{ eco.name }}</strong>
              <span>{{ eco.slogan }}</span>
            </button>
          </div>
        </div>

        <router-link to="/personas" class="nav-link" active-class="active">合伙人画像</router-link>
        <router-link to="/join" class="nav-link" active-class="active">加入流程</router-link>
      </nav>

      <!-- Auth actions -->
      <div class="public-auth-actions">
        <template v-if="isLoggIn && currentUser">
          <div class="nav-user">
            <div class="nav-avatar">{{ currentUser.realname ? currentUser.realname.charAt(0) : '易' }}</div>
            <div class="nav-user-meta">
              <b>{{ currentUser.realname || '用户' }}</b>
              <span>{{ currentUser.mobilePhoneStr || '' }}</span>
            </div>
          </div>
          <router-link to="/portal" class="nav-login">进入工作台</router-link>
          <button class="nav-logout" @click="handleLogout">退出登录</button>
        </template>
        <template v-else>
          <router-link to="/login" class="nav-login">登录 / 进入工作台</router-link>
          <router-link to="/register?eco=partner" class="nav-cta">免费注册合伙人</router-link>
        </template>
      </div>

      <!-- 移动端切换 -->
      <button class="mobile-toggle" @click="mobileOpen = !mobileOpen">☰</button>
    </div>

    <!-- 移动端菜单 -->
    <div :class="['mobile-menu', { open: mobileOpen }]">
      <button
        class="mobile-link"
        @click="$router.push('/home'); mobileOpen = false"
      >首页</button>
      <div class="mobile-label">四大生态</div>
      <button
        v-for="eco in ecosystems"
        :key="eco.slug"
        class="mobile-link"
        @click="$router.push(`/ecosystem/${eco.slug}`); mobileOpen = false"
      >
        {{ eco.name }}
        <span style="color:#68758f;font-size:11px;margin-left:8px">{{ eco.slogan }}</span>
      </button>
      <button
        class="mobile-link"
        @click="$router.push('/personas'); mobileOpen = false"
      >合伙人画像</button>
      <button
        class="mobile-link"
        @click="$router.push('/join'); mobileOpen = false"
      >加入流程</button>
      <button class="nav-login" style="margin-top:10px;text-align:center" @click="$router.push('/login'); mobileOpen = false">登录 / 进入工作台</button>
      <button class="nav-cta" style="margin-top:8px;text-align:center" @click="$router.push('/register?eco=partner'); mobileOpen = false">免费注册合伙人</button>
    </div>

    <!-- 首访登录引导 -->
    <spotlight-guide
      :visible="guideVisible"
      target=".nav-login"
      title="由此处登录/进入工作台"
      content="登录后可进入统一身份中心，选择或直达您的工作台。"
      @close="closeGuide"
    />
  </header>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import SpotlightGuide from '@/components/overlay/SpotlightGuide.vue';
import { getGuideSeen, setGuideSeen } from '@/localCache/cookie';

export default {
  name: 'AppNavbar',
  components: { SpotlightGuide },
  data() {
    return {
      isScrolled: false,
      mobileOpen: false,
      guideVisible: false
    };
  },
  computed: {
    ...mapState('ecosystem', ['ecosystems']),
    ...mapGetters(['isLoggIn', 'currentUser'])
  },
  mounted() {
    window.addEventListener('scroll', this.onScroll);
    this.onScroll();
    this.tryShowGuide();
  },
  watch: {
    '$route.path'(path) {
      if (path !== '/home' && this.guideVisible) {
        this.closeGuide();
      } else {
        this.tryShowGuide();
      }
    }
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.onScroll);
  },
  methods: {
    ...mapActions(['logout']),
    onScroll() {
      this.isScrolled = window.scrollY > 24;
    },
    /**
     * @description: 首访未登录进入首页时展示登录引导
     */
    tryShowGuide() {
      if (this.guideVisible || this.isLoggIn) return;
      if (this.$route.path !== '/home') return;
      if (getGuideSeen()) return;
      this.$nextTick(() => {
        const el = document.querySelector('.nav-login');
        if (!el || !el.getBoundingClientRect || el.getBoundingClientRect().width === 0) return;
        setTimeout(() => {
          this.guideVisible = true;
          // 展示即记录，之后（含退出登录回首页）不再重复弹出
          setGuideSeen();
        }, 600);
      });
    },
    /**
     * @description: 关闭引导并记录已看过
     */
    closeGuide() {
      this.guideVisible = false;
      setGuideSeen();
    },
    handleLogout() {
      this.logout();
      // 退出后回到首页，避免当前页面组件继续发 API 请求触发拦截器弹"请先登录"
      if (this.$route.path !== '/home') {
        this.$router.push('/home');
      }
    }
  }
};
</script>

<style lang="less" scoped>
.app-navbar {
  position: fixed;
  z-index: 100;
  left: 0;
  right: 0;
  top: 0;
  transition: 0.3s;
  background: linear-gradient(180deg, rgba(5, 11, 24, 0.78), rgba(5, 11, 24, 0));
  border-bottom: 1px solid transparent;

  &.scrolled {
    background: rgba(5, 11, 24, 0.9);
    backdrop-filter: blur(18px);
    border-bottom-color: var(--line);
  }
}

.nav-inner {
  width: var(--max);
  height: 68px;
  margin: auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 26px;
}

.brand {
  display: flex;
  align-items: center;
  gap: 11px;
  cursor: pointer;
  text-decoration: none;
  flex-shrink: 0;
}
.logo {
  width: 38px;
  height: 38px;
  border-radius: 11px;
  background: linear-gradient(135deg, var(--cyan), #2875ec);
  display: grid;
  place-items: center;
  box-shadow: 0 10px 26px rgba(50, 190, 255, 0.25);
  flex-shrink: 0;

  &::before {
    content: "易";
    color: #fff;
    font-weight: 800;
    font-size: 18px;
  }
}
.brand-cn {
  font-weight: 800;
  letter-spacing: 0.08em;
  line-height: 1.1;
  color: #fff;
}
.brand-en {
  font-size: 9px;
  color: #7f8ba6;
  letter-spacing: 0.18em;
  margin-top: 3px;
}

.desktop-nav {
  display: flex;
  align-items: center;
  gap: 30px;

}

.nav-link {
  border: 0;
  background: none;
  padding: 21px 0;
  color: #b7c2da;
  font-size: 14px;
  cursor: pointer;
  transition: 0.2s;
  text-decoration: none;

  &:hover, &.active {
    color: #fff;
  }
}

.eco-nav-btn {
  border: 0;
  background: none;
  padding: 21px 0;
  color: #b7c2da;
  font-size: 14px;
  cursor: pointer;
  transition: 0.2s;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    color: #fff;
  }
}

.chev {
  font-size: 12px;
  line-height: 1;
  transition: 0.2s;
}

.nav-group {
  position: relative;

  &:hover .chev {
    transform: rotate(180deg);
  }
  &:hover .eco-menu {
    opacity: 1;
    visibility: visible;
    transform: translate(-50%, 0);
  }
}

.eco-menu {
  position: absolute;
  top: 60px;
  left: 50%;
  transform: translate(-50%, 8px);
  width: 288px;
  padding: 9px;
  background: #091426;
  border: 1px solid var(--line);
  border-radius: 16px;
  box-shadow: var(--shadow);
  opacity: 0;
  visibility: hidden;
  transition: 0.22s;

  button {
    display: block;
    width: 100%;
    border: 0;
    background: transparent;
    text-align: left;
    padding: 12px 14px;
    border-radius: 11px;
    cursor: pointer;
    color: #fff;

    &:hover {
      background: rgba(255, 255, 255, 0.06);
    }
    small {
      display: block;
      color: #64718e;
      font-size: 11px;
    }
    strong {
      display: block;
      margin-top: 1px;
    }
    span {
      display: block;
      color: #8e9bb8;
      font-size: 12px;
    }
  }
}

.mobile-toggle {
  display: none;
  background: none;
  border: 0;
  color: #fff;
  font-size: 25px;
  cursor: pointer;

}

.mobile-menu {
  display: none;
  position: fixed;
  z-index: 99;
  top: 68px;
  left: 0;
  right: 0;
  background: rgba(5, 11, 24, 0.98);
  backdrop-filter: blur(18px);
  border-top: 1px solid var(--line);
  padding: 14px 24px 24px;

  &.open {
    display: block;
  }

  button {
    display: block;
    width: 100%;
    text-align: left;
    padding: 11px 4px;
    border: 0;
    background: none;
    color: #dbe5fa;
  }
}

.mobile-link {
  display: block;
  width: 100%;
  text-align: left;
  padding: 11px 4px;
  border: 0;
  background: none;
  color: #dbe5fa;
  text-decoration: none;
  cursor: pointer;
  font-size: 14px;
}

.mobile-label {
  font-size: 11px;
  letter-spacing: 0.18em;
  color: #65718b;
  padding: 10px 4px 2px;
}

// Auth actions
.public-auth-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.nav-login {
  border: 1px solid rgba(255, 255, 255, 0.16);
  background: rgba(255, 255, 255, 0.055);
  color: #e8efff;
  padding: 10px 17px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: 0.25s;
  text-decoration: none;
  display: inline-flex;
  align-items: center;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.3);
    transform: translateY(-2px);
  }
}

.nav-user {
  display: flex;
  align-items: center;
  gap: 10px;
}

.nav-avatar {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: linear-gradient(135deg, #ffd56a, #ff9b02);
  display: grid;
  place-items: center;
  font-weight: 800;
  font-size: 14px;
  color: #07101d;
  flex-shrink: 0;
}

.nav-user-meta {
  b {
    display: block;
    font-size: 13px;
    color: #fff;
  }
  span {
    font-size: 10px;
    color: #6f7d98;
  }
}

.nav-logout {
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: transparent;
  color: #91a0bd;
  padding: 10px 15px;
  border-radius: 999px;
  font-size: 13px;
  cursor: pointer;
  transition: 0.25s;

  &:hover {
    color: #fff;
    border-color: rgba(255, 255, 255, 0.28);
  }
}

// 移动端：隐藏 CTA，但保留登录按钮
</style>
